import CheckmarkCircle2 from '@crehana/compass.icons.outline/CheckmarkCircle2';
import { Avatar } from '@crehana/compass.ui/avatar';
import { cn } from '@crehana/compass.ui/utils';
import { useIsClient } from '@crehana/react-hooks';
import { Media } from '@crehana/web';

import { EnrollmentsCurrentCourse } from '../../components/leader-enrollments-course';
import { useCourseDetailCourse, useCourseDetailTranslation } from '../../hooks';
import { useAssignContentLeaderFeature } from '../../hooks/use-assign-content-leader-feature';
import CourseDescription from '../../sections/CourseDescription';
import TransactionInfo from '../FloatingCard/TransactionInfo';

interface MainSectionProps {
  courseDescription?: string;
  isFromContent?: boolean;
}

const MainSection: React.FC<MainSectionProps> = ({
  courseDescription,
  isFromContent,
}) => {
  const { course, organizationSlug } = useCourseDetailCourse();
  const { t } = useCourseDetailTranslation();
  const isClient = useIsClient();
  const { isAssignContentLeaderEnabled } = useAssignContentLeaderFeature();

  const requirementsToTakeCourse = course?.requirements?.find(
    req => req.position === 4,
  );

  const renderButtons = () => {
    return (
      <div className="tw-mx-auto md:tw-max-w-[344px]">
        <TransactionInfo />
      </div>
    );
  };

  return (
    <header
      className={cn(
        'tw-flex tw-items-center tw-justify-center tw-mx-auto tw-bg-neutral-light-100 md:tw-justify-start tw-max-w-[1152px]',
        isFromContent ? 'md:tw-pt-3.2' : 'md:tw-pt-4',
      )}
    >
      <section
        id="course-detail-main-section"
        className="tw-w-full tw-px-1.6 md:tw-px-0 tw-mb-2 tw-max-w-[74rem]"
      >
        <div id="course-detail-title-section">
          <h1 className="tw-font-h4 tw-font-semibold">
            {`${t('SEO_TITLE')} ${course.title}`}
          </h1>

          {course.professor && (
            <div className="tw-flex tw-gap-1 tw-items-center tw-mt-2">
              <Avatar
                src={course.professor.profilePictureUrl}
                alt={course.professor.fullName}
                size="s"
              />
              <span className="tw-font-subtitle5 tw-text-1.8">
                {course.professor?.fullName}
              </span>
              <span className="tw-h-2 tw-border-l-0.1 tw-border-solid tw-border-neutral-light-400" />
              <span className="tw-text-1.4">
                {course.professor?.occupation}
              </span>
            </div>
          )}

          {courseDescription && isClient && (
            <CourseDescription courseDescription={courseDescription} />
          )}
        </div>

        <Media until="md">{renderButtons()}</Media>

        {isAssignContentLeaderEnabled && (
          <EnrollmentsCurrentCourse
            courseTitle={course.title}
            courseId={course.originalId}
            organizationSlug={organizationSlug}
            withCardContainer={false}
            className={cn(
              // Display (show only on mobile and tablet)
              'md:tw-hidden',
              // Margins and padding
              'tw-mt-2.4 tw-mb-2.4 tw-pb-2.4 md:tw-pb-4.8',
              // Borders
              'tw-border-b tw-border-solid tw-border-neutral-light-400',
            )}
          />
        )}

        <div>
          <p className="tw-font-subtitle5 tw-text-1.8 tw-my-2 tw-leading-2.5">
            {requirementsToTakeCourse?.text}
          </p>

          {requirementsToTakeCourse?.requirement_details?.map(req => (
            <div
              key={req.title}
              className="tw-flex tw-gap-2 tw-items-center tw-border-0.1 tw-border-solid tw-border-primary-light-300 tw-my-1.2 tw-rounded-1"
            >
              <div className="tw-bg-primary-light-100 tw-text-primary-light-400 tw-p-2 tw-rounded-1">
                <CheckmarkCircle2 size={24} className="tw-fill-current" />
              </div>
              <div className="tw-text-1.4 tw-py-1">
                {req.title || (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: req.text || '',
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
    </header>
  );
};

export default MainSection;
